import type { ReactNode } from "react";

import { cx } from "../app/format";

export const glossaryDefinitions = {
  RFI: "Request for information: a question that must be answered before the specification can be finalised.",
  BMS: "Building management system: the central system used to supervise a building's heating, cooling and alarms.",
  BACnet:
    "Building Automation and Control network: the open protocol commonly used by building controls to exchange values and alarms.",
  FCU: "Fan-coil unit: a room heating and cooling unit that uses a fan to move air over a hot or cold coil.",
  UFH: "Underfloor heating: heating pipework or matting installed under the floor.",
  BOM: "Bill of materials: the list of products and quantities needed for the job.",
  IO: "Inputs/outputs: controller connection points for sensors, switches and controlled equipment.",
  Edge: "Automatry Edge: the connected software layer used for features such as dashboards and alerts.",
  Netmaster:
    "Netmaster: the network manager that links Titan controllers to the wider building system.",
  Modbus:
    "Modbus: a common open language that lets controllers, meters and equipment share data over shared wiring.",
  point:
    "Point: one monitored or controlled value, such as a temperature sensor or valve output.",
  "point ledger":
    "Point ledger: the count of controller points used and still available.",
  "spare point":
    "Spare point: a controller connection that is wired and available but not yet used, kept in reserve for future capacity.",
  controller:
    "Controller: the device that reads sensors and controls equipment in a room or plant area.",
  device:
    "Device: a piece of equipment, such as a valve, sensor or display, that a controller monitors or operates.",
  zone: "Zone: an area or item controlled together, such as a room or heating circuit.",
  port: "Port: a single connection on a controller for one sensor, switch or output.",
  terminal:
    "Terminal: a labelled connection point where a wire is fixed onto a controller or device.",
  terminate:
    "Terminate: to connect a wire onto its terminal. “Do not terminate” means leave that connection unused for now.",
  relay:
    "Relay: an electrically operated switch that a controller uses to turn equipment such as pumps or heaters on and off.",
  PSU: "Power supply unit: the unit that converts mains electricity to the low voltage the controls need to run.",
  RDU: "Room display unit: a wall-mounted screen in a room for viewing and adjusting heating, cooling or other settings.",
  "take-off":
    "Take-off: the measured count of rooms, homes and equipment read off the drawings to size the job.",
  schedule:
    "Schedule: an organised table that lists each item, such as products, devices or signals, with its key details.",
  "mapping schedule":
    "Mapping schedule: the table that links every device and signal to the exact controller connection it uses, ready for the installer.",
  commissioning:
    "Commissioning: the on-site setting-up and testing that proves every control works correctly before handover.",
  verifier:
    "Verifier: the engine check that reviews the specification the AI has proposed against the rules and flags anything to confirm."
} as const;

export type GlossaryTermName = keyof typeof glossaryDefinitions;

const termAliases: Record<string, GlossaryTermName> = {
  rfi: "RFI",
  rfis: "RFI",
  bms: "BMS",
  bacnet: "BACnet",
  "bacnet/ip": "BACnet",
  "bacnet ms/tp": "BACnet",
  fcu: "FCU",
  fcus: "FCU",
  ufh: "UFH",
  bom: "BOM",
  io: "IO",
  "i/o": "IO",
  edge: "Edge",
  netmaster: "Netmaster",
  modbus: "Modbus",
  point: "point",
  points: "point",
  "point ledger": "point ledger",
  "point-ledger": "point ledger",
  "spare point": "spare point",
  "spare points": "spare point",
  controller: "controller",
  controllers: "controller",
  device: "device",
  devices: "device",
  zone: "zone",
  zones: "zone",
  port: "port",
  ports: "port",
  terminal: "terminal",
  terminals: "terminal",
  terminate: "terminate",
  terminates: "terminate",
  terminated: "terminate",
  relay: "relay",
  relays: "relay",
  psu: "PSU",
  psus: "PSU",
  rdu: "RDU",
  rdus: "RDU",
  "take-off": "take-off",
  "take-offs": "take-off",
  takeoff: "take-off",
  takeoffs: "take-off",
  "take off": "take-off",
  schedule: "schedule",
  schedules: "schedule",
  "mapping schedule": "mapping schedule",
  "mapping schedules": "mapping schedule",
  commissioning: "commissioning",
  commission: "commissioning",
  commissions: "commissioning",
  commissioned: "commissioning",
  verifier: "verifier",
  verifiers: "verifier"
};

export const glossaryTermNames = Object.keys(glossaryDefinitions) as GlossaryTermName[];

const normaliseTermName = (name: string): string =>
  name.trim().replace(/\s+/g, " ").toLowerCase();

export const definitionForTerm = (name: string): string | undefined => {
  const canonical = termAliases[normaliseTermName(name)];

  return canonical ? glossaryDefinitions[canonical] : undefined;
};

export function Term({
  name,
  children,
  className
}: {
  name: string;
  children?: ReactNode;
  className?: string;
}) {
  const definition = definitionForTerm(name);
  const label = children ?? name;

  if (!definition) {
    return <>{label}</>;
  }

  return (
    <span
      tabIndex={0}
      title={definition}
      aria-label={`${name}: ${definition}`}
      className={cx(
        "group/term relative inline-flex cursor-help items-baseline rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-[#3E6E9A]/45",
        className
      )}
    >
      <span className="border-b border-dotted border-current">{label}</span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 hidden w-[18rem] max-w-[80vw] -translate-x-1/2 rounded-[9px] border border-[#D7E1EA] bg-[var(--surface)] px-3 py-2 text-left text-[11.5px] font-semibold leading-[1.4] text-[var(--ink2)] shadow-[0_16px_34px_-24px_rgba(35,33,30,.85)] group-hover/term:block group-focus/term:block"
      >
        {definition}
      </span>
    </span>
  );
}

// Shared acronym-wrapping text renderer: turns bare mentions of the
// industry acronyms below into the same Term/tooltip treatment used across
// Products/Quote/Export, without ever mutating the source string (safe to
// use over user-authored text such as free-form notes).
//
// Word-boundary matching only (`\b...\b`) — no extra lookahead — so a
// hyphen-joined phrase like "BACnet-to-cloud" still matches "BACnet" and
// gets wrapped; an earlier version excluded hyphen-adjacent matches, which
// silently left exactly that kind of phrase unwrapped.
const glossaryAcronymPattern = /\b(FCUs?|BMS|BACnet)\b/g;

const glossaryNameForAcronym = (value: string): GlossaryTermName => {
  if (/^fcu/i.test(value)) {
    return "FCU";
  }

  if (/^bms/i.test(value)) {
    return "BMS";
  }

  return "BACnet";
};

export function GlossaryAcronymText({ value }: { value: string }) {
  const parts: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of value.matchAll(glossaryAcronymPattern)) {
    const index = match.index ?? 0;
    const label = match[0];

    if (index > lastIndex) {
      parts.push(value.slice(lastIndex, index));
    }

    parts.push(
      <Term key={`${label}-${index}`} name={glossaryNameForAcronym(label)}>
        {label}
      </Term>
    );
    lastIndex = index + label.length;
  }

  if (lastIndex < value.length) {
    parts.push(value.slice(lastIndex));
  }

  return <>{parts.length > 0 ? parts : value}</>;
}

export function GlossaryPanel({
  title = "Plain-English glossary",
  terms = glossaryTermNames
}: {
  title?: string;
  terms?: readonly GlossaryTermName[];
}) {
  return (
    <details className="rounded-[12px] border border-[var(--line)] bg-[var(--surface)] px-4 py-3">
      <summary className="cursor-pointer list-none text-[11px] font-extrabold uppercase tracking-[.1em] text-[var(--faint)] [&::-webkit-details-marker]:hidden">
        {title}
      </summary>
      <dl className="mt-3 grid gap-2 sm:grid-cols-2">
        {terms.map((term) => (
          <div key={term} className="rounded-[9px] bg-[var(--raised2)] px-3 py-2">
            <dt className="text-[11.5px] font-extrabold text-[var(--ink)]">{term}</dt>
            <dd className="m-0 mt-1 text-[11.5px] leading-[1.4] text-[var(--muted)]">
              {glossaryDefinitions[term]}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
